import { FC, useMemo, useState, type JSX } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as invoicesActions from '@payaca/store/invoices/invoicesActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';

import EditInvoiceControl from '@/ui/components/editInvoiceControl/EditInvoiceControl';
import EditInvoicePreviewButton from '@/ui/components/editInvoicePreviewButton/EditInvoicePreviewButton';
import EditInvoiceSaveFeedback from '@/ui/components/editInvoiceSaveFeedback/EditInvoiceSaveFeedback';
import InvoiceSidePanel from '@/ui/components/invoiceSidePanel/InvoiceSidePanel';
import PreviewAndSendInvoiceControl from '@/ui/components/previewAndSendInvoiceControl/PreviewAndSendInvoiceControl';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { Modal } from '@payaca/ambrosia';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import { useDeal, useInvoice } from '@payaca/store/hooks/appState';
import { requestGetUploadsForEntity } from '@payaca/store/uploads/uploadsActions';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './EditInvoicePage.sass';

enum ViewType {
  EDIT = 'edit',
  SEND = 'send',
}

type Props = {
  invoiceId: number;
  projectId: number;
  viewType?: ViewType;
};

const EditInvoicePage: FC<Props> = ({
  invoiceId,
  projectId,
  viewType,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [view, setView] = useState<ViewType>(viewType || ViewType.EDIT);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
  }>();

  const invoice = useInvoice(invoiceId);

  const deal = useDeal(invoice?.dealId);

  useEffect(() => {
    if (!!invoice?.sentAt || !!invoice?.voidedAt) {
      history.push(`/projects/${projectId}/invoices/${invoice.id}`);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'projectId', 'invoice.id', and 'history'
  }, [invoice?.sentAt, invoice?.voidedAt]);

  useEffect(() => {
    if (invoice && invoice.bouncedAt) {
      setView(ViewType.SEND);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'invoice'
  }, [invoice?.bouncedAt]);

  const navigateToView = useMemo(() => {
    return getUrlSearchParam(location.search, 'view');
  }, [location]);

  useEffect(() => {
    if (
      navigateToView === ViewType.SEND &&
      validationResult &&
      validationResult.isValid
    ) {
      setView(ViewType.SEND);
    }
  }, [navigateToView, validationResult]);

  useEffect(() => {
    dispatch(
      invoicesActions.requestGetInvoiceValidationResult(
        invoiceId,
        (validationResult) => {
          setValidationResult(validationResult);
        }
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) {
      dispatch(invoicesActions.requestGetInvoice(invoiceId));

      dispatch(jobContentActions.requestGetJobLineItemsForInvoice(invoiceId));
      dispatch(requestGetUploadsForEntity(invoiceId, 'invoice'));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [invoiceId]);

  useEffect(() => {
    if (projectId) {
      dispatch(requestGetDeal(projectId));
      dispatch(invoicesActions.requestGetInvoiceLinesForDeal(projectId));
      dispatch(invoicesActions.requestGetInvoicesForDeal(projectId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [projectId]);

  useEffect(() => {
    if (
      invoice &&
      !invoice.sentAt &&
      validationResult &&
      !validationResult.isValid
    ) {
      setView(ViewType.EDIT);
    }
  }, [validationResult, view, invoice]);

  useEffect(() => {
    if (invoice && invoice?.dealId) {
      dispatch(dealsActions.requestGetDeal(invoice?.dealId));
      dispatch(
        invoicesActions.requestGetPaymentReconciliationRecordsForDeal(
          invoice?.dealId
        )
      );
      dispatch(
        jobPaymentsActions.requestGetJobPaymentsForDeal(invoice?.dealId)
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'invoice'
  }, [invoice?.dealId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(customerActions.requestGetCustomer(deal?.customerId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  return (
    <AuthenticatedPageWrapper
      className="edit-invoice-page"
      breadcrumbItems={
        invoice
          ? [
              {
                label: 'Back',
                to: `/projects/${projectId}/invoices-and-payments`,
              },
              { label: 'Edit invoice' },
            ]
          : undefined
      }
    >
      <div className="p-4">
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values */}
        <div className="grid @screen-lg:grid-cols-[1fr_25rem]">
          <div>
            {invoice && (
              <>
                <EditInvoiceControl invoiceId={invoiceId} />
                <Modal
                  isOpen={view === ViewType.SEND}
                  title="Send invoice"
                  size="xl"
                  onClose={() => setView(ViewType.EDIT)}
                >
                  <Modal.Body>
                    <PreviewAndSendInvoiceControl invoiceId={invoiceId} />
                  </Modal.Body>
                </Modal>
              </>
            )}
          </div>
          <div className="p-4 @screen-lg:sticky @screen-lg:top-0 @screen-lg:self-start">
            <div className="edit-invoice-sidebar">
              <EditInvoicePreviewButton
                invoiceId={invoiceId}
                proceedToPreview={() => {
                  if (deal) {
                    dispatch(
                      invoicesActions.requestGetInvoiceLinesForDeal(deal.id)
                    );
                  }
                  setView(ViewType.SEND);
                }}
              />
              <EditInvoiceSaveFeedback invoiceId={invoiceId} />
              <InvoiceSidePanel invoiceId={invoiceId} />
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedPageWrapper>
  );
};
export default EditInvoicePage;
